import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { current_password: '', new_password: '', confirm_password: '' };

/**
 * Reusable "Change my password" form.
 * Works for any authenticated user (admin or member).
 */
export default function ChangePasswordForm({ onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const strength = (() => {
    const pw = form.new_password;
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[A-Z]/.test(pw)) score += 1;
    if (/\d/.test(pw)) score += 1;
    if (/[^A-Za-z0-9]/.test(pw)) score += 1;
    return Math.min(score, 4);
  })();

  const strengthLabels = ['', 'Faible', 'Moyen', 'Bon', 'Excellent'];
  const strengthColors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('La confirmation ne correspond pas au nouveau mot de passe.');
      return;
    }
    if (form.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, { withCredentials: true });
      toast.success('Mot de passe modifié avec succès.');
      setDone(true);
      setForm(EMPTY);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur lors du changement de mot de passe.';
      toast.error(typeof detail === 'string' ? detail : 'Erreur inattendue.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="card text-center" data-testid="change-password-success">
        <CheckCircle2 className="text-[#4A6B48] mx-auto mb-3" size={48} />
        <h3 className="text-xl font-bold text-[#2E332D] mb-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Mot de passe modifié
        </h3>
        <p className="text-sm text-[#5C665A] mb-4">
          Votre mot de passe a été mis à jour. Toutes les autres sessions actives ont été déconnectées
          par sécurité.
        </p>
        <button onClick={() => setDone(false)} className="btn-secondary" data-testid="change-password-again">
          Modifier à nouveau
        </button>
      </div>
    );
  }

  const renderInput = (key, label, showKey) => (
    <div>
      <label className="block text-sm font-bold text-[#2E332D] mb-1">{label}</label>
      <div className="relative">
        <input
          type={show[showKey] ? 'text' : 'password'}
          required
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="w-full px-4 py-2 pr-10 border border-[#E5E3DB] rounded-lg"
          data-testid={`change-password-${key.replace(/_/g, '-')}-input`}
          autoComplete={key === 'current_password' ? 'current-password' : 'new-password'}
        />
        <button
          type="button"
          onClick={() => setShow((s) => ({ ...s, [showKey]: !s[showKey] }))}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C665A] hover:text-[#2E332D]"
          tabIndex={-1}
          aria-label={show[showKey] ? 'Masquer' : 'Afficher'}
          data-testid={`toggle-${showKey}-visibility`}
        >
          {show[showKey] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="card space-y-4" data-testid="change-password-form">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full bg-[#4A6B48]/10 flex items-center justify-center shrink-0">
          <KeyRound className="text-[#4A6B48]" size={20} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Changer mon mot de passe
          </h3>
          <p className="text-xs text-[#5C665A]">Au moins 8 caractères, 1 lettre et 1 chiffre.</p>
        </div>
      </div>

      {renderInput('current_password', 'Mot de passe actuel', 'current')}
      {renderInput('new_password', 'Nouveau mot de passe', 'next')}

      {form.new_password && (
        <div className="space-y-1" data-testid="password-strength">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}
                className={`h-1.5 flex-1 rounded-full ${i <= strength ? strengthColors[strength] : 'bg-[#E5E3DB]'}`} />
            ))}
          </div>
          <p className="text-xs text-[#5C665A]">Force : <strong>{strengthLabels[strength]}</strong></p>
        </div>
      )}

      {renderInput('confirm_password', 'Confirmer le nouveau mot de passe', 'confirm')}

      {form.confirm_password && form.new_password !== form.confirm_password && (
        <p className="text-xs text-red-500" data-testid="password-mismatch">
          Les deux mots de passe ne correspondent pas.
        </p>
      )}

      <div className="flex items-start gap-2 bg-[#F9F8F6] p-3 rounded text-xs text-[#5C665A]">
        <ShieldCheck size={16} className="text-[#4A6B48] mt-0.5 shrink-0" />
        <span>Pour votre sécurité, toutes les autres sessions actives seront déconnectées après le changement.</span>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="change-password-submit"
        className="btn-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
      >
        {submitting ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />}
        {submitting ? 'Modification…' : 'Modifier le mot de passe'}
      </button>
    </form>
  );
}
